import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';
import { Tag, Plus, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL } from "../../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const AdminPromoCodes = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [codes, setCodes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: '', description: '', discount_type: 'percentage', discount_value: 10, max_uses: 0, valid_until: '' });
  const headers = { Authorization: `Bearer ${token}` };

  const fetchCodes = async () => {
    try { const res = await axios.get(`${API}/admin/promo-codes`, { headers }); setCodes(res.data); } catch {}
  };
  useEffect(() => { fetchCodes(); }, []);

  const createCode = async () => {
    if (!form.code.trim()) { toast({ title: 'Code is required', variant: 'destructive' }); return; }
    try {
      await axios.post(`${API}/admin/promo-codes`, form, { headers });
      toast({ title: 'Promo code created' });
      setShowForm(false);
      setForm({ code: '', description: '', discount_type: 'percentage', discount_value: 10, max_uses: 0, valid_until: '' });
      fetchCodes();
    } catch (err) { toast({ title: 'Error', description: err.response?.data?.detail, variant: 'destructive' }); }
  };

  const toggleCode = async (id, active) => {
    await axios.put(`${API}/admin/promo-codes/${id}`, { is_active: !active }, { headers });
    fetchCodes();
  };

  const deleteCode = async (id) => {
    if (!window.confirm('Delete this promo code?')) return;
    await axios.delete(`${API}/admin/promo-codes/${id}`, { headers });
    toast({ title: 'Deleted' });
    fetchCodes();
  };

  return (
    <div data-testid="admin-promo-codes-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Promo Codes</h1>
          <p className="text-gray-500 text-sm">Create and manage discount codes</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="create-promo-btn"><Plus className="w-4 h-4 mr-2" />New Code</Button>
      </div>

      {showForm && (
        <Card className="bg-[#132218] border-emerald-900/30 mb-6" data-testid="promo-form">
          <CardContent className="p-5 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div><Label className="text-gray-400 text-xs">Code</Label><Input value={form.code} onChange={e => setForm({...form, code: e.target.value.toUpperCase()})} placeholder="SAVE20" className="bg-black/20 border-emerald-900/30 text-white mt-1" data-testid="promo-code-input" /></div>
              <div>
                <Label className="text-gray-400 text-xs">Type</Label>
                <select value={form.discount_type} onChange={e => setForm({...form, discount_type: e.target.value})} className="w-full mt-1 h-9 rounded-md bg-black/20 border border-emerald-900/30 text-white text-sm px-3" data-testid="promo-type-select">
                  <option value="percentage">Percentage Off</option>
                  <option value="fixed">Fixed Amount Off</option>
                  <option value="trial_extension">Trial Extension (days)</option>
                </select>
              </div>
              <div><Label className="text-gray-400 text-xs">Value</Label><Input type="number" value={form.discount_value} onChange={e => setForm({...form, discount_value: parseFloat(e.target.value) || 0})} className="bg-black/20 border-emerald-900/30 text-white mt-1" data-testid="promo-value-input" /></div>
              <div><Label className="text-gray-400 text-xs">Max Uses (0=unlimited)</Label><Input type="number" value={form.max_uses} onChange={e => setForm({...form, max_uses: parseInt(e.target.value) || 0})} className="bg-black/20 border-emerald-900/30 text-white mt-1" data-testid="promo-max-uses-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-gray-400 text-xs">Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Holiday discount" className="bg-black/20 border-emerald-900/30 text-white mt-1" /></div>
              <div><Label className="text-gray-400 text-xs">Valid Until (optional)</Label><Input type="date" value={form.valid_until} onChange={e => setForm({...form, valid_until: e.target.value})} className="bg-black/20 border-emerald-900/30 text-white mt-1" /></div>
            </div>
            <Button onClick={createCode} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-promo-btn">Create Promo Code</Button>
          </CardContent>
        </Card>
      )}

      <Card className="bg-[#132218] border-emerald-900/30">
        <CardContent className="p-0">
          <table className="w-full">
            <thead><tr className="border-b border-emerald-900/30">
              <th className="text-left text-xs text-gray-500 font-medium p-3">Code</th>
              <th className="text-left text-xs text-gray-500 font-medium p-3">Type</th>
              <th className="text-left text-xs text-gray-500 font-medium p-3">Value</th>
              <th className="text-left text-xs text-gray-500 font-medium p-3">Uses</th>
              <th className="text-left text-xs text-gray-500 font-medium p-3">Status</th>
              <th className="text-right text-xs text-gray-500 font-medium p-3">Actions</th>
            </tr></thead>
            <tbody>
              {codes.map(c => (
                <tr key={c.id} className="border-b border-emerald-900/10" data-testid={`promo-row-${c.id}`}>
                  <td className="p-3"><span className="font-mono text-emerald-400 text-sm font-bold">{c.code}</span><p className="text-gray-500 text-xs">{c.description}</p></td>
                  <td className="p-3 text-gray-400 text-sm capitalize">{c.discount_type?.replace('_', ' ')}</td>
                  <td className="p-3 text-white text-sm">{c.discount_type === 'percentage' ? `${c.discount_value}%` : c.discount_type === 'fixed' ? `$${c.discount_value}` : `${c.discount_value} days`}</td>
                  <td className="p-3 text-gray-400 text-sm">{c.uses}/{c.max_uses || '∞'}</td>
                  <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-xs ${c.is_active ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>{c.is_active ? 'Active' : 'Inactive'}</span></td>
                  <td className="p-3 text-right flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => toggleCode(c.id, c.is_active)} className="text-gray-400 hover:text-white">{c.is_active ? <ToggleRight className="w-4 h-4 text-emerald-400" /> : <ToggleLeft className="w-4 h-4" />}</Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteCode(c.id)} className="text-gray-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></Button>
                  </td>
                </tr>
              ))}
              {codes.length === 0 && <tr><td colSpan={6} className="text-center text-gray-500 py-8">No promo codes yet</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPromoCodes;
